import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import ProductsTag from '@/components/feature/template-tags/ProductsTag';

interface RestaurationTagProps {
  limit?: number;
}

/** Mots-clés qui identifient une catégorie de restauration. */
const RESTAURANT_KEYWORDS = /resto|restaurant|plat|menu|carte|entr[eé]e|dessert|boisson|pizza|burger|tacos|grill|caf[eé]|p[aâ]tisserie|sandwich|cuisine|traiteur|food/i;

/**
 * Balise {{restauration}} — carte du restaurant du marchand.
 * Réutilise les produits du catalogue dont la catégorie est une catégorie
 * de restauration (plats, menus, boissons, desserts…). Rien à afficher = bloc masqué.
 */
export default function RestaurationTag({ limit }: RestaurationTagProps) {
  const { tenant, isTenant } = useTenant();
  const merchantId = tenant?.id ?? null;

  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isTenant || !merchantId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await supabase.from('product_categories').select('id, name');
        const ids = ((data || []) as { id: number; name: string | null }[])
          .filter((c) => c.name && RESTAURANT_KEYWORDS.test(c.name))
          .map((c) => c.id);
        if (!cancelled) setCategoryIds(ids);
      } catch {
        /* silencieux */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isTenant, merchantId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
      </div>
    );
  }

  if (categoryIds.length === 0) return null;

  return (
    <section className="w-full px-4 md:px-6 py-10">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-50 text-secondary-700 text-xs font-medium mb-3">
            <i className="ri-restaurant-2-line"></i>Restauration
          </span>
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">Notre carte</h2>
          <p className="text-sm text-foreground-500 mt-2 max-w-xl mx-auto">
            Plats, menus et boissons à commander directement en ligne.
          </p>
        </div>
        <ProductsTag categoryIds={categoryIds} limit={limit} />
      </div>
    </section>
  );
}